import { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useApplicationDetail,
  useComments,
  useCreateComment,
  useDeleteComment,
  useResolveComment,
  useUpdateComment,
  communicationKeys,
} from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { comment } from '@axo/shared/data-access/types';

export interface ICommentState {
  callID?: string;
  key: string;
  original: comment.Comment | null;
  edit: comment.CreateComment;
  isNew: boolean;
  isEditing: boolean;
}

export interface ICommentsState {
  state: ICommentState[];
  hasLoaded: boolean;
  addNewComment: (template?: comment.CommentTemplate, callID?: string) => void;
  saveComment: (comment: ICommentState) => void;
  cancelComment: (comment: ICommentState) => void;
  setEditComment: (comment: ICommentState) => void;
  editComment: (
    comment: ICommentState
  ) => (editComment: comment.CreateComment) => void;
  removeComment: (comment: ICommentState) => void;
  markAsResolved: (comment: ICommentState) => void;
}

let key = 0;

export function useCommentsState(applicationID: string): ICommentsState {
  const { state } = useContext(DataAccessContext);
  const application = useApplicationDetail(applicationID);
  const comments = useComments({ LoanApplicationID: applicationID });
  const [commentsState, setCommentsState] = useState<ICommentState[]>([]);
  const [currentLoanApplication, setCurrentLoanApplication] = useState('');
  const createComment = useCreateComment();
  const updateComment = useUpdateComment(applicationID);
  const deleteComment = useDeleteComment(applicationID);
  const resolveComment = useResolveComment(applicationID);
  const queryClient = useQueryClient();

  const initialComment: comment.CreateComment = {
    Title: '',
    Body: '',
    LoanApplicationID: applicationID,
    MarketCountry: application.data?.MarketCountry || '',
    OwnerID: state.user.clientID,
  };

  const createInitialCommentsState = (comments: comment.Comment[]) => {
    return comments.map<ICommentState>((comment) => ({
      key: comment.ID,
      callID: comment.CallCommunicationID,
      original: comment,
      edit: comment,
      isNew: false,
      isEditing: false,
    }));
  };

  useEffect(() => {
    const items = comments.data?.Items;
    if (!items?.length) return;
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setCommentsState((prevState) => {
      const fixedComments = prevState.filter((_comment) => !_comment.isNew);
      return items.length !== fixedComments.length
        ? createInitialCommentsState(items)
        : prevState;
    });
  }, [comments.data]);

  useMemo(() => {
    if (comments.data && currentLoanApplication !== applicationID) {
      setCommentsState(createInitialCommentsState(comments.data.Items));
      setCurrentLoanApplication(applicationID);
    }
  }, [
    setCommentsState,
    comments.data,
    currentLoanApplication,
    setCurrentLoanApplication,
    applicationID,
  ]);

  const updateCommentState = (
    compare: keyof ICommentState,
    originalState: ICommentState,
    updatedState: ICommentState
  ) => {
    setCommentsState((prevState) =>
      prevState.map((comment) => {
        if (comment[compare] === originalState[compare]) {
          return updatedState;
        } else {
          return comment;
        }
      })
    );
  };

  const addHoursToCurrentDate = (hours: number) => {
    const newDate = new Date();
    newDate.setHours(newDate.getHours() + hours);
    return newDate;
  };

  return {
    state: commentsState,
    hasLoaded: currentLoanApplication === applicationID,
    addNewComment: (template?: comment.CommentTemplate, callID?: string) => {
      setCommentsState((prevState) => [
        {
          callID,
          original: null,
          key: `${++key}`,
          edit: {
            ...initialComment,
            CallCommunicationID: callID,
            ID: new Date().toISOString(),
            Title: template?.Title ?? '',
            Body: template?.Body || '',
            TemplateID: template?.ID,
            FollowupAt: template?.FollowUpDelayHours
              ? addHoursToCurrentDate(template.FollowUpDelayHours).toISOString()
              : undefined,
          },
          isNew: true,
          isEditing: true,
        },
        ...prevState,
      ]);
    },

    saveComment: (comment: ICommentState) => {
      if (comment.isNew) {
        createComment
          .mutateAsync({
            ...comment.edit,
            FollowupAt: comment.edit.FollowupAt || undefined,
          })
          .then((updated) => {
            void queryClient.invalidateQueries(
              communicationKeys.comments({ applicationID })
            );

            updateCommentState('edit', comment, {
              ...comment,
              original: updated,
              edit: updated,
              isNew: false,
              isEditing: false,
            });
          });
      } else {
        updateComment
          .mutateAsync({
            ...comment.edit,
            ID: comment.original?.ID,
          })
          .then((updated) => {
            void queryClient.invalidateQueries(
              communicationKeys.comments({ applicationID })
            );

            updateCommentState('edit', comment, {
              ...comment,
              original: updated,
              edit: updated,
              isNew: false,
              isEditing: false,
            });
          });
      }
    },
    cancelComment: (comment: ICommentState) => {
      if (comment.isNew) {
        setCommentsState((prevState) => prevState.filter((c) => c !== comment));
      } else {
        updateCommentState('original', comment, {
          ...comment,
          edit: comment.original as comment.CreateComment,
          isEditing: false,
          isNew: false,
        });
      }
    },
    setEditComment: (comment: ICommentState) => {
      updateCommentState('original', comment, {
        ...comment,
        isEditing: true,
      });
    },
    editComment:
      (comment: ICommentState) => (editComment: comment.CreateComment) => {
        updateCommentState('edit', comment, {
          ...comment,
          edit: editComment,
        });
      },

    removeComment: (comment: ICommentState) => {
      if (comment.original) {
        deleteComment.mutateAsync(comment.original.ID).then(() => {
          setCommentsState((prevState) =>
            prevState.filter((c) => c !== comment)
          );
        });
      }
    },

    markAsResolved: (comment: ICommentState) => {
      if (comment.original)
        resolveComment.mutateAsync(comment.original.ID).then((updated) =>
          updateCommentState('original', comment, {
            ...comment,
            original: updated,
            edit: updated,
            isNew: false,
            isEditing: false,
          })
        );
    },
  };
}
